import * as Sentry from '@sentry/browser';
import { mapValues } from 'lodash-es';
import { useEffect } from 'react';

import { withoutQueryString } from './utils';

// Navigation breadcrumbs use from/to, fetch and XHR breadcrumbs use url
const URL_BREADCRUMB_KEYS = new Set(['from', 'to', 'url']);

function withoutFrameQueryStrings(
  stacktrace: Sentry.Stacktrace | undefined,
): Sentry.Stacktrace | undefined {
  return (
    stacktrace && {
      ...stacktrace,
      frames: stacktrace.frames?.map((frame) => ({
        ...frame,
        filename: frame.filename && withoutQueryString(frame.filename),
      })),
    }
  );
}

export function withoutEventQueryStrings(
  event: Sentry.ErrorEvent,
): Sentry.ErrorEvent {
  return {
    ...event,
    request: event.request && {
      ...event.request,
      url: event.request.url && withoutQueryString(event.request.url),
    },
    exception: event.exception && {
      ...event.exception,
      values: event.exception.values?.map((value) => ({
        ...value,
        stacktrace: withoutFrameQueryStrings(value.stacktrace),
      })),
    },
  };
}

export default function initSentry() {
  const uriComponents = window.location.hostname.split('.');
  // Results in ouraring, ourastaging, ourasandbox
  const environment =
    uriComponents.length > 1 ? uriComponents[1] : uriComponents[0];

  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    release: process.env.RELEASE_VERSION,
    environment,
    // User data may be HIPAA protected, so collect as little as possible.
    // Query strings can hold one-time secrets (OAuth codes, invite tokens).
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: {
        request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
      },
      httpBodies: [],
      urlQueryParams: false,
    },
    integrations: [Sentry.browserSessionIntegration({ lifecycle: 'route' })],
    // The SDK doesn't apply dataCollection to the page URL of events, to stack
    // frames from inline scripts (whose filename is the page URL) or to
    // navigation and fetch breadcrumbs
    beforeSend: withoutEventQueryStrings,
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
        // Hide asserts and console.logs. Asserts get Severity.Log level:
        // https://github.com/getsentry/sentry-javascript/blob/4bfbf99daea758ec18db11da7d96590dc7d6f1b5/packages/types/src/severity.ts#L27
        return null;
      }
      if (!breadcrumb.data) {
        return breadcrumb;
      }
      return {
        ...breadcrumb,
        data: mapValues(breadcrumb.data, (value, key) =>
          URL_BREADCRUMB_KEYS.has(key) && typeof value === 'string'
            ? withoutQueryString(value)
            : value,
        ),
      };
    },
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function logException(ex: unknown, context?: any) {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setExtras(context);
    }
    Sentry.captureException(ex);
  });

  if (window.console && console.error) {
    console.error(ex);
  }
}

interface SentryUserContextProps {
  userUID?: string;
}

/**
 * A React component that sets the user Sentry context
 */
export function SentryUserContext({ userUID }: SentryUserContextProps) {
  useEffect(() => {
    if (userUID && process.env.ENABLE_SENTRY) {
      Sentry.setUser({
        id: userUID,
      });
    }
  }, [userUID]);

  return null;
}
